import { Component, OnInit, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NewsletterService } from '../../services/newsletter.service';
import { ToastService } from '../../services/toast.service';
import { ErrorMapperService } from '../../services/error-mapper.service';
import { ActionThrottleService } from '../../core/action-throttle.service';

@Component({
  selector: 'app-unsubscribe',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './unsubscribe.component.html',
  styleUrls: ['./unsubscribe.component.scss']
})
export class UnsubscribeComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private newsletterService = inject(NewsletterService);
  private toastService = inject(ToastService);
  private errorMapper = inject(ErrorMapperService);
  private fb = inject(FormBuilder);
  private throttle = inject(ActionThrottleService);

  // Populated only when the link came from an email template (?email=...).
  // When absent, the visitor is unsubscribing from the site itself and
  // types their email into emailForm instead.
  email: string | null = null;
  isProcessing = false;
  requestSubmitted = false;
  errorMessage = '';
  currentYear = new Date().getFullYear();

  emailForm: FormGroup = this.fb.group({
    email: ['', [Validators.required, Validators.email]]
  });

  ngOnInit(): void {
    this.email = this.route.snapshot.queryParamMap.get('email');
  }

  get emailControl() {
    return this.emailForm.get('email');
  }

  get emailFieldError(): string {
    if (this.emailControl?.hasError('required')) {
      return 'Email address is required';
    }
    if (this.emailControl?.hasError('email')) {
      return 'Please enter a valid email address';
    }
    return '';
  }

  shouldShowFieldError(): boolean {
    const field = this.emailControl;
    return !!(field && field.invalid && (field.dirty || field.touched));
  }

  async submitUnsubscribeRequest(): Promise<void> {
    const result = await this.throttle.run('unsubscribe-request', () => this.sendRequest(), { cooldownMs: 10_000 })
      .catch(() => undefined);
    if (result && result.blocked && result.reason === 'cooldown') {
      this.errorMessage = `Please wait ${this.throttle.waitSeconds(result)}s before trying again.`;
    }
  }

  private async sendRequest(): Promise<void> {
    let targetEmail = this.email;

    if (!targetEmail) {
      if (this.emailForm.invalid) {
        this.emailForm.markAllAsTouched();
        return;
      }
      targetEmail = (this.emailControl?.value ?? '').trim();
    }

    if (!targetEmail) {
      this.errorMessage = 'Invalid email address';
      return;
    }

    this.isProcessing = true;
    this.errorMessage = '';

    try {
      await this.newsletterService.requestUnsubscribe(targetEmail);
      this.requestSubmitted = true;
      this.toastService.success(
        'Your unsubscribe request has been sent to our team. You will be removed from our mailing list shortly.',
        'Request Submitted'
      );
    } catch (error: any) {
      console.error('Original error (unsubscribe):', error);
      const mapped = this.errorMapper.map(error);
      this.errorMessage = mapped.userMessage || 'Failed to submit unsubscribe request';
      this.toastService.error(this.errorMessage, 'Error');
    } finally {
      this.isProcessing = false;
    }
  }
}